/**
 * Shared March HTTP API base URL from Vite env.
 */

import { isDevIamLoginMode } from '../auth/authMode';
// SEC-I5. Re-exported so existing importers of this module keep one place to
// look for API-env questions; the definition lives in a leaf module to avoid
// an import cycle with tokenLifecycle.
import { isCookieAuthMode } from '../auth/cookieAuthMode';
import { getDevAccessToken, getStoredAccessToken } from '../auth/tokenStore';
import { getValidAccessToken } from '../auth/tokenLifecycle';

export { isCookieAuthMode };

const IAM_DISABLED_VALUES = new Set(['1', 'true', 'yes', 'on']);

/** True when protected March API calls should include a Bearer token. */
export function hasMarchAgentBearer(): boolean {
  if (isCookieAuthMode()) return false;
  return getMarchRuntimeBearer().length > 0;
}

/** True when protected March API calls must not run without authentication. */
export function requiresMarchAgentBearer(): boolean {
  if (isIamDisabled()) return false;
  return isDevIamLoginMode();
}

/** Temporary demo/presentation kill switch — bypass SMART gate and Bearer injection. */
export function isIamDisabled(): boolean {
  const raw = (
    import.meta.env.VITE_IAM_DISABLED ??
    import.meta.env.VITE_AUTH_DISABLED ??
    ''
  )
    .toString()
    .trim()
    .toLowerCase();
  return IAM_DISABLED_VALUES.has(raw);
}

/**
 * Resolve Bearer token for March API calls (sync).
 * Prefer {@link getMarchRuntimeBearerAsync} before protected calls when refresh may be needed.
 */
export function getMarchRuntimeBearer(): string {
  if (isIamDisabled()) return '';
  if (typeof window === 'undefined') return '';

  if (isDevIamLoginMode()) {
    return getDevAccessToken();
  }

  const envBearer = (import.meta.env.VITE_COMMAND_API_BEARER ?? '').toString().trim();
  if (envBearer) return envBearer;

  try {
    const fromSession = (window.sessionStorage.getItem('march_access_token') ?? '').trim();
    if (fromSession) return fromSession;
    return (window.localStorage.getItem('march_access_token') ?? '').trim();
  } catch {
    return '';
  }
}

/** Async bearer resolution with refresh-before-expiry for dev Keycloak IAM. */
export async function getMarchRuntimeBearerAsync(): Promise<string> {
  if (isIamDisabled()) return '';
  if (isDevIamLoginMode()) {
    return await getValidAccessToken();
  }
  return getMarchRuntimeBearer();
}

export function marchApiUrlRaw(): string {
  return (
    import.meta.env.VITE_MARCH_API_URL ??
    import.meta.env.VITE_COMMAND_API_URL ??
    ''
  )
    .toString()
    .trim();
}

export function marchApiStageRaw(): string {
  return (import.meta.env.VITE_MARCH_API_STAGE ?? '').toString().trim();
}

/** Explicit same-origin mode (relative URLs). */
export function isMarchApiSameOrigin(): boolean {
  const r = marchApiUrlRaw().toLowerCase();
  return r === 'same' || r === 'relative';
}

/** Any March / command API base configured (including same-origin sentinel). */
export function isMarchHttpApiConfigured(): boolean {
  return marchApiUrlRaw().length > 0;
}

/**
 * Base URL with no trailing slash. Empty string means same-origin relative paths (`/health`, …).
 */
export function getMarchHttpApiBase(): string {
  const raw = marchApiUrlRaw();
  if (!raw) return '';
  if (isMarchApiSameOrigin()) return '';

  let url = raw.replace(/\/+$/, '');
  const stage = marchApiStageRaw();
  if (stage) return `${url}/${stage.replace(/^\/+|\/+$/g, '')}`;
  return url;
}

/**
 * True when the hostname looks like S3 REST or website hosting — same-origin `/api/*`
 * requests never reach CloudFront's ALB proxy, so API checks fail with `VITE_MARCH_API_URL=same`.
 */
export function isLikelyS3StaticUiHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (!h) return false;
  return (
    (h.includes('.s3.') && h.endsWith('.amazonaws.com')) || h.includes('s3-website')
  );
}

/** Whether a stored access token exists (ignores near-expiry; used for boot checks). */
export function hasStoredMarchAccessToken(): boolean {
  if (isDevIamLoginMode()) {
    return getStoredAccessToken().length > 0;
  }
  return getMarchRuntimeBearer().length > 0;
}
